import { AllianceEditDialog } from '@/components/alliances/AllianceEditDialog';
import { getTeamIcon } from '@/utils/teamIcons';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyTableState,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Pencil, Plus, Shield, Trash2 } from 'lucide-react';
import { useState } from 'react';

/** Shared mono micro-label chrome for the stat column headers. */
const MICRO_LABEL =
  'font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em] text-muted-foreground';

/** Fixed-width right-aligned stat cell so header + rows line up down a column. */
const STAT_CELL = 'w-16 shrink-0 text-right';

interface AllianceTeam {
  id: string;
  name: string;
  iconName: string;
  iconColor: string;
}

interface Alliance {
  id: string;
  eventId: string;
  name: string;
  color: string | null;
  iconName: string;
  iconColor: string;
  teamCount: number;
  totalScore: number;
  challengesCompleted: number;
  teams: AllianceTeam[];
}

interface AlliancesListProps {
  eventId: string;
}

/**
 * List of alliances for an event with CRUD actions
 */
export function AlliancesList({ eventId }: AlliancesListProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editTarget, setEditTarget] = useState<Alliance | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Alliance | null>(null);

  const { data, isLoading } = useQuery<{ success: boolean; data: Alliance[] }>({
    queryKey: ['EventAlliances', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/alliances`),
    enabled: !!eventId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (allianceId: string) => {
      return apiClient.delete(`/events/${eventId}/alliances/${allianceId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventAlliances', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
      toast.success('Alliance deleted');
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to delete alliance');
    },
  });

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id);
    setDeleteTarget(null);
  };

  const alliances = data?.data || [];

  if (isLoading) {
    return (
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Alliances
            </CardTitle>
            <CardDescription>
              {alliances.length === 0
                ? 'Group teams into alliances for aggregate scoring'
                : `${alliances.length} alliance${alliances.length !== 1 ? 's' : ''} configured`}
            </CardDescription>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} size="sm" icon={Plus}>
            Add Alliance
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {alliances.length === 0 ? (
            <EmptyTableState
              icon={<Shield className="h-8 w-8" />}
              title="No alliances yet"
              description="Create alliances to group teams into competing divisions."
              action={{
                label: 'Add Alliance',
                onClick: () => setShowCreateDialog(true),
                icon: Plus,
              }}
            />
          ) : (
            <>
              {/* Column header — aligns to the stat cells in each row. */}
              <div className="hidden items-center gap-3 border-b bg-muted/20 px-4 py-2 sm:flex">
                <span className="h-8 w-8 shrink-0" aria-hidden="true" />
                <span className={`min-w-0 flex-1 ${MICRO_LABEL}`}>Alliance</span>
                <span className={`${STAT_CELL} ${MICRO_LABEL}`}>Points</span>
                <span className={`${STAT_CELL} ${MICRO_LABEL}`}>Done</span>
                <span className={`${STAT_CELL} ${MICRO_LABEL}`}>Teams</span>
                <span className="h-8 w-8 shrink-0" aria-hidden="true" />
              </div>

              <ul className="divide-y">
                {alliances.map((alliance) => {
                  const AllianceIcon = getTeamIcon(alliance.iconName);
                  return (
                    <li key={alliance.id} className="px-4 py-2.5 transition-colors hover:bg-muted/50">
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                          style={{ backgroundColor: `${alliance.iconColor}20` }}
                        >
                          <AllianceIcon className="h-4 w-4" style={{ color: alliance.iconColor }} />
                        </div>

                        <div className="min-w-0 flex-1">
                          <span className="truncate text-sm font-medium text-foreground">
                            {alliance.name}
                          </span>
                          {/* Mobile-only meta — stat columns collapse below sm. */}
                          <p className="mt-0.5 text-xs text-muted-foreground sm:hidden">
                            {alliance.totalScore} pts · {alliance.challengesCompleted} done ·{' '}
                            {alliance.teamCount} team{alliance.teamCount !== 1 ? 's' : ''}
                          </p>
                        </div>

                        <span className={`${STAT_CELL} hidden font-mono text-sm tabular-nums text-foreground sm:block`}>
                          {alliance.totalScore.toLocaleString()}
                        </span>
                        <span className={`${STAT_CELL} hidden font-mono text-sm tabular-nums text-muted-foreground sm:block`}>
                          {alliance.challengesCompleted}
                        </span>
                        <span className={`${STAT_CELL} hidden font-mono text-sm tabular-nums text-muted-foreground sm:block`}>
                          {alliance.teamCount}
                        </span>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                              aria-label={`Actions for ${alliance.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditTarget(alliance)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit Alliance
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget(alliance)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Alliance
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Team chips — indented to align under the name. */}
                      {alliance.teams.length > 0 && (
                        <div className="mt-2 ml-11 flex flex-wrap gap-1.5">
                          {alliance.teams.map((team) => (
                            <span
                              key={team.id}
                              className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium"
                            >
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: team.iconColor }}
                              />
                              {team.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Alliance"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? Teams will be unassigned but not deleted.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />

      {showCreateDialog && (
        <AllianceEditDialog
          eventId={eventId}
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
        />
      )}

      {editTarget && (
        <AllianceEditDialog
          eventId={eventId}
          alliance={editTarget}
          open={!!editTarget}
          onOpenChange={(open) => !open && setEditTarget(null)}
        />
      )}
    </>
  );
}
